import { useEffect, useState } from "react";
import api from "@/api";
import { useAuth, ROLE_LABELS } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Check, X, Clock } from "lucide-react";

export default function LinksPage() {
  const { subaccount } = useAuth();
  const [list, setList] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    target_role: "coord_colonia",
    target_region_name: "",
    municipio_clave: "",
    max_uses: 100,
    notes: "",
  });

  const load = async () => {
    const r = await api.get("/links");
    setList(r.data);
  };

  useEffect(() => {
    load();
    if (subaccount?.entidad_clave) {
      api.get("/catalog/municipios", { params: { entidad_clave: subaccount.entidad_clave } })
        .then((r) => setMunicipios(r.data));
    }
  }, [subaccount?.entidad_clave]);

  const isStatal = subaccount?.scope === "estatal";
  const allowedTargets = (subaccount?.enabled_roles || []).filter(
    (r) => r === "coord_regional" || r === "coord_colonia"
  );

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        municipio_clave: form.municipio_clave ? parseInt(form.municipio_clave, 10) : null,
        max_uses: parseInt(form.max_uses, 10) || 100,
      };
      await api.post("/links", payload);
      toast.success("Enlace creado · pendiente de aprobación");
      setOpen(false);
      setForm({ target_role: "coord_colonia", target_region_name: "", municipio_clave: "", max_uses: 100, notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/links/${id}/approve`, {});
      toast.success("Enlace aprobado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/links/${id}/reject`, {});
      toast.success("Enlace rechazado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const buildUrl = (code) => `${window.location.origin}/r/${code}`;

  return (
    <div className="space-y-6" data-testid="links-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="tracking-label text-blue-700">Reclutamiento</span>
          <h1 className="mt-2 font-display font-black text-3xl text-slate-900">Enlaces</h1>
          <p className="text-sm text-slate-600 mt-1">
            Genera enlaces para coordinadores regionales o de colonia. Cada uno
            requiere aprobación antes de poder usarse.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="links-create-button">
              <Plus className="w-4 h-4 mr-1.5" /> Generar enlace
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nuevo enlace de reclutamiento</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="links-form">
              <div>
                <Label>Rol que se asignará al voluntario</Label>
                <Select value={form.target_role} onValueChange={(v) => setForm({ ...form, target_role: v })}>
                  <SelectTrigger data-testid="links-target-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedTargets.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isStatal && (
                <div>
                  <Label>Municipio</Label>
                  <Select value={String(form.municipio_clave || "")} onValueChange={(v) => setForm({ ...form, municipio_clave: v })}>
                    <SelectTrigger data-testid="links-muni-select"><SelectValue placeholder="Selecciona municipio" /></SelectTrigger>
                    <SelectContent>
                      {municipios.map((m) => (
                        <SelectItem key={m.municipio_clave} value={String(m.municipio_clave)}>{m.municipio_nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>{form.target_role === "coord_colonia" ? "Nombre de la colonia" : "Nombre de la zona/región"}</Label>
                <Input required value={form.target_region_name} onChange={(e) => setForm({ ...form, target_region_name: e.target.value })} data-testid="links-region-input" placeholder={form.target_role === "coord_colonia" ? "Ej. Centro" : "Ej. Zona Norte"} />
              </div>
              <div>
                <Label>Máximo de usos</Label>
                <Input type="number" min={1} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="links-max-uses" />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="links-notes" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" className="btn-primary" data-testid="links-submit">Solicitar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-card overflow-hidden">
        {list.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-10 h-10 mx-auto text-slate-400" />
            <p className="mt-3 text-slate-600">Sin enlaces generados todavía.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {list.map((l) => (
              <div key={l.id} className="px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`link-row-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${
                      l.status === "approved" ? "bg-green-100 text-green-800 border-green-200" :
                      l.status === "pending" ? "bg-yellow-100 text-yellow-800 border-yellow-200" :
                      "bg-red-100 text-red-800 border-red-200"
                    } hover:bg-opacity-100`}>{l.status}</Badge>
                    <Badge variant="outline" className="uppercase tracking-wider text-[10px]">{ROLE_LABELS[l.target_role]}</Badge>
                    <span className="font-medium text-slate-900">{l.target_region_name}</span>
                    {l.municipio_nombre && <span className="text-xs text-slate-500">· {l.municipio_nombre}</span>}
                  </div>
                  <div className="mt-1.5 text-xs text-slate-600">
                    Solicitado por <strong>{l.generated_by_name}</strong> ({ROLE_LABELS[l.generated_by_role]}) el {new Date(l.created_at).toLocaleString("es-MX")}
                  </div>
                  {l.approved_at && (
                    <div className="text-xs text-green-700">
                      Aprobado por <strong>{l.approved_by_name}</strong> el {new Date(l.approved_at).toLocaleString("es-MX")}
                    </div>
                  )}
                  {l.rejected_at && (
                    <div className="text-xs text-red-700">
                      Rechazado por <strong>{l.approved_by_name}</strong> el {new Date(l.rejected_at).toLocaleString("es-MX")}
                    </div>
                  )}
                  {l.status === "approved" && (
                    <div className="mt-2 flex items-center gap-2">
                      <Input value={buildUrl(l.code)} readOnly className="text-xs h-8" data-testid={`link-url-${l.id}`} />
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(buildUrl(l.code)); toast.success("Copiado"); }} data-testid={`link-copy-${l.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{l.used_count}/{l.max_uses}</span>
                    </div>
                  )}
                </div>
                {l.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approve(l.id)} data-testid={`link-approve-${l.id}`}>
                      <Check className="w-4 h-4 mr-1" /> Aprobar
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => reject(l.id)} data-testid={`link-reject-${l.id}`}>
                      <X className="w-4 h-4 mr-1" /> Rechazar
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
